import { notification } from "antd";
import Helper from "../../Helper";
import { ProcurementFilter } from "../../models/ProcurementFilter";
import { ProcurementView } from "../../models/ProcurementView";
import { plotFetch } from "../Helper/ApiRequest";
import { ChangeHistoryPoint } from "../Materials/materialTypes";

export function getMaterialAudits(
    team_subdomain: string,
    project_token: string,
    include_system_updates?: boolean
): Promise<ChangeHistoryPoint[]> {
    const url = `${Helper.baseUrlAltSubdomain(
        team_subdomain ? team_subdomain : "projects"
    )}/projects/${project_token}/material_audits?include_system_updates=${include_system_updates ? "true" : "false"}`;

    return plotFetch(url, { method: "get" }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }

            return [];
        })
        .then((response) => {
            const history = new Array<ChangeHistoryPoint>();

            for (const point of response) {
                history.push(ChangeHistoryPoint.fromApiObject(point));
            }

            return history;
        })
        .catch((e) => {
            notification.error({
                message: "Failed to get material audits",
                description: "Please try again later",
            });

            return [];
        });
}

export function getProcurementViews(
    subdomain: string,
    projectToken: string,
    timezone: string
): Promise<ProcurementView[]> {
    const url = `${Helper.baseUrlAltSubdomain(
        subdomain ? subdomain : "projects"
    )}/projects/${projectToken}/procurement_views`;

    return plotFetch(url, { method: "get" }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else {
                throw new Error("Network error.");
            }
        })
        .then((response) => {
            return response.map((view: any) => {
                return ProcurementView.fromApiObject(view, timezone);
            });
        })
        .catch(() => {
            notification.error({
                message: "Failed to get procurement views",
                description: "Please try again later",
            });

            return [];
        });
}

export function updateProcurementView(
    subdomain: string,
    projectToken: string,
    timezone: string,
    view: ProcurementView
): Promise<ProcurementView> {
    const url = `${Helper.baseUrlAltSubdomain(
        subdomain ? subdomain : "projects"
    )}/projects/${projectToken}/procurement_views/${view.id}`;

    return plotFetch(
        url,
        {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(view.toApiObject()),
        },
        true
    )
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return ProcurementView.fromApiObject(response, timezone);
        })
        .catch(() => {
            notification.error({
                message: "Failed to update procurement view",
                description: "Please try again later",
            });
            return view;
        });
}

export function createProcurementView(
    subdomain: string,
    projectToken: string,
    timezone: string,
    view: ProcurementView
): Promise<ProcurementView> {
    const url = `${Helper.baseUrlAltSubdomain(
        subdomain ? subdomain : "projects"
    )}/projects/${projectToken}/procurement_views/`;

    return plotFetch(url, { method: "POST", body: JSON.stringify(view.toApiObject()) }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return ProcurementView.fromApiObject(response, timezone);
        })
        .catch(() => {
            notification.error({
                message: "Failed to create procurement view",
                description: "Please try again later",
            });
            return view;
        });
}

export function destroyProcurementView(subdomain: string, projectToken: string, view: ProcurementView): Promise<any> {
    const url = `${Helper.baseUrlAltSubdomain(
        subdomain ? subdomain : "projects"
    )}/projects/${projectToken}/procurement_views/${view.id}`;

    return plotFetch(url, { method: "delete" }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return Promise.resolve();
        })
        .catch(() => {
            notification.error({
                message: "Failed to delete procurement view",
                description: "Please try again later",
            });
            return view;
        });
}

export const getProcurementFilters = (project: any): Promise<ProcurementFilter[]> => {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/procurement_filters`;

    return plotFetch(url, { method: "get" }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return response.map((filter: any) => ProcurementFilter.fromApiObject(filter, project.timezone));
        })
        .catch(() => {
            notification.error({
                message: "Failed to fetch procurement filters",
                description: "Please try again later",
            });
            return [];
        });
};
export const getProcurementFilterById = (project: any, filterId: number): Promise<ProcurementFilter | null> => {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/procurement_filters/${filterId}`;

    return plotFetch(url, { method: "get" }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return ProcurementFilter.fromApiObject(response, project.timezone);
        })
        .catch(() => {
            notification.error({
                message: `Failed to fetch procurement filter #${filterId}`,
                description: "Please try again later",
            });
            return null;
        });
};

export const createProcurementFilter = (project: any, filter: ProcurementFilter): Promise<ProcurementFilter> => {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/procurement_filters`;

    return plotFetch(url, { method: "post", body: JSON.stringify(filter.altToApiObject()) }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return ProcurementFilter.fromApiObject(response, project.timezone);
        })
        .catch(() => {
            notification.error({
                message: "Failed to create procurement filter",
                description: "Please try again later",
            });
            return filter;
        });
};

export const updateProcurementFilter = (project: any, filter: ProcurementFilter): Promise<ProcurementFilter> => {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/procurement_filters/${filter.id}`;

    return plotFetch(url, { method: "put", body: JSON.stringify(filter.altToApiObject()) }, true)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error("Network error.");
        })
        .then((response) => {
            return ProcurementFilter.fromApiObject(response, project.timezone);
        })
        .catch(() => {
            notification.error({
                message: "Failed to update procurement filter",
                description: "Please try again later",
            });
            return filter;
        });
};

export const deleteProcurementFilter = (project: any, filter: ProcurementFilter): Promise<void> => {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/procurement_filters/${filter.id}`;

    return plotFetch(url, { method: "delete" }, true)
        .then((response) => {
            if (response.ok) {
                return Promise.resolve();
            }
            throw new Error("Network error.");
        })
        .catch(() => {
            notification.error({
                message: "Failed to delete procurement filter",
                description: "Please try again later",
            });
            return Promise.reject();
        });
};

export function sendProcurementActionItemsEmail(project: any): Promise<void> {
    const url = `${Helper.baseUrlAltSubdomain(
        project.claimed_by_team_subdomain || "projects"
    )}/projects/${project.unique_token}/action_items_email_send`;

    return plotFetch(url, { method: "post" }, true)
        .then((response) => {
            if (response.ok) {
                return Promise.resolve();
            }
            throw new Error("Network error.");
        })
        .catch(() => {
            notification.error({
                message: "Failed to send procurement action items email",
                description: "Please try again later",
            });
            return Promise.reject();
        });
}
